import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import type { PortfolioHealthSlice } from '@/hooks/useSyncedData';
import { useLocalization } from '@/hooks/useLocalization';

const COLOR_BY_NAME: Record<string, string> = {
  'On Track': 'hsl(160, 84%, 39%)',
  'At Risk': 'hsl(38, 92%, 50%)',
  Critical: 'hsl(346, 77%, 50%)',
};
const colorFor = (name: string) => COLOR_BY_NAME[name] ?? 'hsl(240, 5%, 65%)';

interface PortfolioChartProps {
  data: PortfolioHealthSlice[];
}

const PortfolioChart = ({ data: portfolioHealthData }: PortfolioChartProps) => {
  const { t } = useLocalization();
  const visible = portfolioHealthData.filter((d) => d.value > 0);
  const total = portfolioHealthData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <div className="masira-card masira-slide-up" style={{ animationDelay: '400ms' }}>
        <h3 className="font-semibold text-foreground mb-4">{t('Portfolio Health')}</h3>
        <p className="text-sm text-muted-foreground py-12 text-center">{t('No projects synced yet.')}</p>
      </div>
    );
  }

  return (
    <div className="masira-card masira-slide-up" style={{ animationDelay: '400ms' }}>
      <h3 className="font-semibold text-foreground mb-4">{t('Portfolio Health')}</h3>

      <div className="h-48 md:h-60 relative">
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-semibold text-foreground leading-none">{total}</span>
          <span className="text-xs text-muted-foreground mt-1">{t('projects')}</span>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={visible}
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="88%"
              paddingAngle={visible.length > 1 ? 3 : 0}
              cornerRadius={4}
              dataKey="value"
              stroke="none"
            >
              {visible.map((d) => (
                <Cell key={d.name} fill={colorFor(d.name)} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap justify-center gap-3 md:gap-6 mt-2">
        {portfolioHealthData.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: colorFor(item.name) }}
            />
            <span className="text-xs md:text-sm text-muted-foreground">
              {item.name} ({item.value})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortfolioChart;
